import type { ConnectionItemProps } from "@wayflyer/flyui";
import { Button, ConnectionItem, Flex, SidePanel, Text } from "@wayflyer/flyui";
import { RhfDropdownField } from "@wayflyer/flyui-hook-form";
import { useIntl } from "react-intl";
import { useBankConnectionLogoResolvers, useBankConnectionLogos } from "bankconnect";

import { ConfirmAccounts } from "../../pages/banking/bank-connect-controller/confirm-accounts/confirm-accounts";
import { BankConnectContextProvider } from "../../pages/banking/bank-connect-controller/context/bank-connect-provider";
import { BankLogoDisplay } from "../bank-logo-display";
import { COMMON_MESSAGES } from "../common-messages";
import { useBankControllerContext } from "../../pages/banking/hooks/bank-controller-context";

import { AuOpenBankingBanner } from "./au-open-banking-banner";
import { MESSAGES } from "./message-definitions";

interface BankConnectionsSlideoutProps {
  isOpen: boolean;
}

export const BankConnectionsSlideout = ({ isOpen }: BankConnectionsSlideoutProps) => {
  const intl = useIntl();
  const {
    ready,
    bankConnectProps,
    bankingConnections,
    countryOptions,
    countryForm,
    defaultCountry,
    basiqClient,
    isConnectionUnsupportedCountry,
    isConnecting,
    shouldConfirmAccountSelection,
    confirmAccountsBankConnections,
    handleConnect,
    closeConnectionsSlideout,
  } = useBankControllerContext();

  const logoResolvers = useBankConnectionLogoResolvers({
    getInstitutionLogoPath: bankConnectProps.plaidConnectProps.getInstitutionLogoPath,
    basiqClient,
  });
  const { logoLoadingStates } = useBankConnectionLogos({
    bankConnections: bankingConnections,
    resolvers: logoResolvers,
  });

  const totalAccountCount = bankingConnections.reduce(
    (acc, conn) => acc + conn.accounts.filter((a) => a.pull_enabled).length,
    0
  );

  const connectionItems: ConnectionItemProps[] = [...bankingConnections]
    .sort((a, b) => (a.institutionName > b.institutionName ? 1 : a.institutionName === b.institutionName ? 0 : -1))
    .map((connection) => {
      const pullEnabledAccounts = connection.accounts.filter((account) => account.pull_enabled);
      return {
        testId: connection.id,
        label: connection.institutionName,
        logo: <BankLogoDisplay logoState={logoLoadingStates[connection.institutionID]} />,
        subtitle: pullEnabledAccounts.length ? (
          <ul>
            {pullEnabledAccounts.map((account) => (
              <li key={account.id}>
                {account.mask && intl.formatMessage(MESSAGES.accountEnding, { mask: account.mask })}
              </li>
            ))}
          </ul>
        ) : undefined,
        badgeStatus: "positive" as const,
        badgeText: intl.formatMessage(COMMON_MESSAGES.connected),
      } satisfies ConnectionItemProps;
    });

  const selectedCountryCode = countryForm.watch("country");

  return (
    <SidePanel
      open={isOpen}
      isModal={false}
      closeOnClickElseWhere={false}
      onClose={closeConnectionsSlideout}
      title={intl.formatMessage(MESSAGES.connectionsPanelTitle)}
      variant="md"
      primaryAction={{
        content: intl.formatMessage(MESSAGES.doneButton),
        onAction: closeConnectionsSlideout,
      }}
    >
      <Flex direction="column" gap="8">
        <Text>{intl.formatMessage(MESSAGES.connectionsPanelDescription)}</Text>
        {shouldConfirmAccountSelection && confirmAccountsBankConnections.length > 0 && (
          <BankConnectContextProvider bankConnectSchema={bankConnectProps}>
            <ConfirmAccounts bankConnections={confirmAccountsBankConnections} />
          </BankConnectContextProvider>
        )}

        {!shouldConfirmAccountSelection && (
          <Flex direction="column" gap="3">
            <Flex align="center" gap="2">
              <Text fontWeight="semiBold">{intl.formatMessage(MESSAGES.connectedAccountsHeading)}</Text>
              <Text color="placeholder">{totalAccountCount}</Text>
            </Flex>
            <Flex direction="column" gap="2">
              {connectionItems.map((item) => (
                <ConnectionItem key={item.testId} {...item} />
              ))}
            </Flex>
          </Flex>
        )}

        <Flex direction="column" gap="4" align="flex-start">
          <Text fontWeight="semiBold">{intl.formatMessage(MESSAGES.addAnotherAccountHeading)}</Text>
          <RhfDropdownField
            name="country"
            initialValue={defaultCountry?.code}
            options={countryOptions}
            form={countryForm}
            label={intl.formatMessage(MESSAGES.bankLocationLabel)}
          />
          {bankConnectProps.basiqConnectProps.openBankingEnabled && selectedCountryCode === "AU" && (
            <AuOpenBankingBanner />
          )}
          <Button
            variant="Secondary"
            onClick={handleConnect}
            disabled={isConnectionUnsupportedCountry}
            loading={!ready || isConnecting}
          >
            {intl.formatMessage(MESSAGES.addAnotherAccountButton)}
          </Button>
        </Flex>
      </Flex>
    </SidePanel>
  );
};
